// External Dependencies
import { Routes, Route } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useLayoutEffect } from 'react';

// Internal Dependencies
import { updateDeviceInnerWidth } from 'state/actions';
import AdminActions from 'pages/AdminActions';
import AdminPortalShow from 'pages/AdminPortals/Show';
import AdminPortalsPage from 'pages/AdminPortals/All';
import ApiPlayground from 'pages/ApiPlayground';
import Auth from 'components/Auth';
import BaseLayout from 'components/BaseLayout';
import DashboardPage from 'pages/Dashboard';
import DistrictLicenses from 'pages/AdminPortals/Show/Licenses/All';
import DistrictLicensesNew from 'pages/AdminPortals/Show/Licenses/New';
import DistrictsNew from 'pages/AdminPortals/New';
import EstimateShow from 'pages/Estimates/Show';
import EstimatesPage from 'pages/Estimates/All';
import FeatureFlagsPage from 'pages/ServerSettings/FeatureFlags';
import FeeCalculator from 'pages/AdminActions/FeeCalculator';
import FeedbackPage from 'pages/Feedback/All';
import FeedbackShowPage from 'pages/Feedback/Show';
import ImportOrganizations from 'pages/AdminPortals/ImportOrganizations';
import ImportShow from 'pages/Imports/Show';
import Imports from 'pages/Imports/All';
import InventoryVendorsPage from 'pages/Vendors/InventoryVendors/All';
import InvoiceShow from 'pages/Invoices/Show';
import InvoicesPage from 'pages/Invoices/All';
import LoginPage from 'pages/Login';
import LogoutPage from 'pages/Logout';
import OrganizationHeatMap from 'pages/Organizations/HeatMap';
import OrganizationShow from 'pages/Organizations/Show';
import OrganizationsPage from 'pages/Organizations/All';
import ScheduledCronJobsPage from 'pages/ScheduledCronJobs';
import Search from 'pages/Search';
import SendEmail from 'pages/AdminActions/SendEmail';
import ServerSettingsPage from 'pages/ServerSettings';
import StripeWebhookEvent from 'pages/AdminActions/StripeWebhookEvent';
import StripeWebhookEvents from 'pages/AdminActions/StripeWebhookEvents';
import TaxExemptFormNew from 'pages/AdminPortals/TaxExemptForm/New';
import UpdateAppSettings from 'pages/AdminActions/UpdateAppSettings';
import Vendors from 'pages/Vendors';
import usePrevious from 'hooks/usePrevious';
import useWindowSize from 'hooks/useWindowSize';

// Component Definition
const Router = (): JSX.Element => {
  const dispatch = useDispatch();

  // Get the current device width to store in the Redux state tree
  const { innerWidth } = useWindowSize();

  const previousInnerWidth = usePrevious(innerWidth);

  useLayoutEffect(() => {
    if (previousInnerWidth !== innerWidth) {
      dispatch(updateDeviceInnerWidth(innerWidth));
    }
  }, [dispatch, innerWidth, previousInnerWidth]);

  return (
    <Routes>
      <Route
        element={<BaseLayout hideNavigation />}
        path="/"
      >
        <>
          <Route
            element={<LoginPage />}
            path="/"
          />
          <Route
            element={<LoginPage />}
            path="/login"
          />
        </>
      </Route>

      <Route
        element={<BaseLayout />}
        path="/"
      >
        <>
          {/* Private routes */}
          <Route
            element={<Auth component={LogoutPage} />}
            path="/logout"
          />
          <Route
            element={<Auth component={DashboardPage} />}
            path="/dashboard"
          />

          {/* Admin Portals */}
          <Route
            path="/admin_portals"
          >
            <Route
              element={<Auth component={AdminPortalsPage} />}
              path="/admin_portals"
            />
            <Route
              element={<Auth component={AdminPortalShow} />}
              path="/admin_portals/:id"
            />
            <Route
              element={<Auth component={DistrictsNew} />}
              path="/admin_portals/new"
            />
            <Route
              element={<Auth component={DistrictLicenses} />}
              path="/admin_portals/:adminPortalId/licenses"
            />
            <Route
              element={<Auth component={DistrictLicensesNew} />}
              path="/admin_portals/:adminPortalId/licenses/new"
            />
            <Route
              element={<Auth component={TaxExemptFormNew} />}
              path="/admin_portals/:adminPortalId/tax-exempt-form/new"
            />
            <Route
              element={<Auth component={ImportOrganizations} />}
              path="/admin_portals/:adminPortalId/import-organizations"
            />
          </Route>

          {/* Organizations */}
          <Route
            path="/organizations"
          >
            <Route
              element={<Auth component={OrganizationsPage} />}
              path="/organizations"
            />
            <Route
              element={<Auth component={OrganizationShow} />}
              path="/organizations/:id"
            />
            <Route
              element={<Auth component={OrganizationHeatMap} />}
              path="/organizations/heat_map"
            />
          </Route>

          <Route path="/imports">
            <Route
              element={<Auth component={Imports} />}
              path="/imports"
            />

            <Route
              element={<Auth component={ImportShow} />}
              path="/imports/:id"
            />
          </Route>

          <Route
            element={<Auth component={ScheduledCronJobsPage} />}
            path="/scheduled_cron_jobs"
          />

          <Route
            element={<Auth component={ApiPlayground} />}
            path="/api_playground"
          />

          {/* Server Settings */}
          <Route
            path="/server_settings"
          >
            <Route
              element={<Auth component={ServerSettingsPage} />}
              path="/server_settings"
            />
            <Route
              element={<Auth component={FeatureFlagsPage} />}
              path="/server_settingsfeature_flags"
            />
          </Route>

          {/* Feedback */}
          <Route
            path="/feedback"
          >
            <Route
              element={<Auth component={FeedbackPage} />}
              path="/feedback"
            />
            <Route
              element={<Auth component={FeedbackShowPage} />}
              path="/feedback:id"
            />
          </Route>

          {/* Admin Actions */}
          <Route
            path="/admin_actions"
          >
            <Route
              element={<Auth component={AdminActions} />}
              path="/admin_actions"
            />
            <Route
              element={<Auth component={SendEmail} />}
              path="/admin_actions/send_email"
            />
            <Route
              element={<Auth component={StripeWebhookEvents} />}
              path="/admin_actions/stripe_webhook_events"
            />
            <Route
              element={<Auth component={StripeWebhookEvent} />}
              path="/admin_actions/stripe_webhook_events/:id"
            />
            <Route
              element={<Auth component={UpdateAppSettings} />}
              path="/admin_actions/update_app_settings"
            />
            <Route
              element={<Auth component={FeeCalculator} />}
              path="/admin_actions/fee_calculator"
            />
          </Route>

          <Route
            element={<Auth component={Search} />}
            path="/search"
          />

          <Route path="/estimates">
            <Route
              element={<Auth component={EstimatesPage} />}
              path="/estimates"
            />
            <Route
              element={<Auth component={EstimateShow} />}
              path="/estimates/:id"
            />
          </Route>

          <Route path="/invoices">
            <Route
              element={<Auth component={InvoicesPage} />}
              path="/invoices"
            />
            <Route
              element={<Auth component={InvoiceShow} />}
              path="/invoices/:id"
            />
          </Route>

          <Route path="/vendors">
            <Route
              element={<Auth component={Vendors} />}
              path="/vendors"
            />
            <Route
              element={<Auth component={InventoryVendorsPage} />}
              path="/vendors/inventory_vendors"
            />
          </Route>
        </>
      </Route>
    </Routes>
  );
};

export default Router;
